import React, { JSX, useRef } from 'react';
import { FC, MouseEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { find, startCase } from 'lodash';
import classNames from 'classnames';
import {
  Button,
  Heading,
  Icon,
  IconSvg,
  Image,
  Link,
  Select,
  sortVariantByPrice,
  StrainInfo,
} from '@/components';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { GProduct, Variant } from '@/types';
import { NOT_DEFINED } from '@/constants';
import {
  currencyFormat,
  formatSpecialFlowerType,
  formatWeightLabel,
} from '@/utils';
import { useAnalytics, useUiStore } from '@/data';
import {
  useActiveProductBadges,
  useFeaturedFlag,
  useKeyboardCarouselNavigation,
  useWishList,
} from '@/hooks';
import { useProductContext } from './context';

import { ISelectOption } from '../Select/Select';
import { IProductProps } from './Product';
import { CompoundType, UnitPricingTier } from './context/ProductContext';
import ProductBadge from './ProductBadge';

import style from './product.module.scss';

const ProductLinkWrapper: FC<ProductLinkWrapperProps> = ({
  className,
  product,
  analyticsListName,
  isInternalWrapper,
  children,
}) => {
  const [, setState] = useUiStore();
  const { measureProductClick, measureSelectItemGA4 } = useAnalytics();

  const trackingClick = () => {
    setState({ is_search_open: false });
    measureProductClick(product, 'products-cards');
    measureSelectItemGA4(product, analyticsListName ?? 'product-cards');
  };

  return (
    <Link
      aria-label={`Go to ${product.name}`}
      className={classNames(className, {
        [style.product__link_wrapper]: isInternalWrapper,
      })}
      href={product?.url ?? ''}
      onClick={trackingClick}
    >
      {children}
    </Link>
  );
};

// Render Image of Product
const ProductImage: FC<IProductProps> = ({ className, product }) => {
  const [load, setLoad] = useState(true);
  const stockPhoto = product?.asset?.stock ?? false;

  const REVERSE_PROXY_LINK = process.env.NEXT_PUBLIC_REVERSE_PROXY_LINK ?? '';

  return (
    <div className={classNames(style.product__link_image, className)}>
      <figure
        className={classNames(style.product__image, {
          [style.product__image__load]: load,
        })}
      >
        <Image
          alt={product?.name ?? 'Product image'}
          blurDataURL={
            // eslint-disable-next-line max-len
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1' viewBox='0 0 1 1'%3E%3Crect fill='#ededed' width='1' height='1'/%3E%3C/svg%3E\")"
          }
          fallback={`${REVERSE_PROXY_LINK}/images/product-placeholder.svg`}
          fill
          onLoad={() => {
            setLoad(false);
          }}
          placeholder="blur"
          src={product?.asset?.image as string}
          title={product?.name as string}
        />
        {stockPhoto && (
          <span className={style.product__in_stock}>Stock photo</span>
        )}
      </figure>
    </div>
  );
};

// Render Name of Product
const ProductName: FC<{ name: string; className?: string }> = ({
  name,
  className,
}) => {
  return (
    <Heading className={classNames(style.product__name, className)} level={5}>
      {name}
    </Heading>
  );
};

// Render Active Badge of Product
const ProductBadgeContainer: FC<ProductBadgeContainerProps> = ({
  className,
  product,
  weightVariant,
}) => {
  const { badges, shouldRender } = useActiveProductBadges({
    product,
    weightVariant,
  });

  const renderedBadges = useMemo(
    () =>
      badges.map(({ props }, index) => <ProductBadge {...props} key={index} />),
    [badges],
  );

  if (!shouldRender || badges.length === 0) return null;

  return (
    <ul className={classNames(style.product__badges, className)}>
      {renderedBadges}
    </ul>
  );
};

//Render default product info
export const getDefaultProductInfoComponents = (
  product: GProduct,
  compoundList: CompoundType[],
): JSX.Element[] => {
  return [
    product?.flower_type && (
      <ProductCategory info={product.flower_type} key="category" />
    ),
    compoundList.length > 0 && (
      <ProductCannabinoidProfile
        compoundList={compoundList}
        key="info-product"
      />
    ),
    product?.brand_name && (
      <ProductInfo info={product.brand_name} key="info-brand" type="solid" />
    ),
    product?.subcategory && (
      <ProductInfo
        info={startCase(product.subcategory)}
        key="subcategory"
        type="solid"
      />
    ),
  ].filter(Boolean) as JSX.Element[];
};

//Render Cannabinoid Profile
const ProductCannabinoidProfile: FC<ProductInfoListProps> = ({
  compoundList,
  badgeMode = 'filled',
  badgeClass,
  initDot,
}) => {
  if (!compoundList) return null;

  const isDefault = badgeMode === 'default';

  return (
    <>
      {compoundList.map(({ type, value }) => {
        if (!value) return null;

        const info = (
          <ProductInfo
            className={classNames(style.info, badgeClass)}
            icon={type.toLowerCase()}
            info={isDefault ? `${type}: ${value}` : value}
            key={type}
            type={badgeMode || 'filled'}
          />
        );

        if (badgeMode !== 'default') {
          return info;
        }

        return (
          <div className={style.info__item_conteiner} key={type}>
            {initDot && <div className={style.info__dot} />}
            {info}
          </div>
        );
      })}
    </>
  );
};

// Render Related Info of Product
const ProductInfo: FC<ProductInfoProps> = ({
  className,
  label,
  info,
  showLabel,
  icon,
  type = 'filled',
}) => {
  if (showLabel) {
    return (
      <div className={classNames(style.info__container, className)}>
        {label && <label>{label}</label>}
        <p>{info || NOT_DEFINED}</p>
      </div>
    );
  }

  return (
    <span
      className={classNames(style.info, className, {
        [style.info__filled]: type === 'filled',
        [style.info__default]: type === 'default',
        [style.info__solid]: type === 'solid',
      })}
    >
      {icon && type !== 'default' && <IconSvg name={icon} />}{' '}
      {info || NOT_DEFINED}
    </span>
  );
};

// Render Category of Product
const ProductCategory: FC<ProductInfoProps> = ({
  className,
  label,
  info,
  showLabel,
}) => {
  if (!info) {
    return null;
  }

  if (showLabel) {
    return (
      <div className={classNames(style.category__container, className)}>
        {label && <label>{label}</label>}
        <p>{formatSpecialFlowerType(info) || NOT_DEFINED}</p>
      </div>
    );
  }

  return (
    <StrainInfo className={classNames(style.info, className)} strain={info} />
  );
};

// Render THC,CBD,TAC Info of Product
const ProductInfoPack: FC<ProductInfoPackProps> = ({
  className,
  label,
  compoundList,
  showIcon,
}) => {
  if (compoundList?.length === 0) return null;

  return (
    <div className={classNames(style.info__container, className)}>
      {label && <label>{label}</label>}
      <div className={style.info__body}>
        {compoundList?.map(({ type, value }) => (
          <div
            className={classNames(style.info__pack, className, {
              [style.info__pack_badge]: !label,
              [style.info__pack_icon]: !showIcon,
            })}
            key={type}
          >
            {showIcon && <IconSvg name={type.toLowerCase()} />}
            <span>{type}</span>
            {value}
          </div>
        ))}
      </div>
    </div>
  );
};

// Render Price of Product
const ProductPrice: FC<ProductPriceProps> = ({
  className,
  weightVariant,
  elementVariants,
  isOverflow = false,
  isSpecial,
  type,
}) => {
  const { unitPricingTier } = useProductContext();
  const { isTaxAppliedMessage, showStriketroughPrice } = useFeaturedFlag();

  const labelSalePrice = `$${currencyFormat(
    unitPricingTier?.price
      ? unitPricingTier?.price * 100
      : weightVariant?.salePrice ?? 0,
  )}`;
  const labelPrice = `$${currencyFormat(weightVariant?.price ?? 0)}`;
  const discount = `${weightVariant?.discount_percent}%`;

  const priceMoreVariation = (
    <div
      className={classNames(style.price__ins_container, {
        [style.price__ins_container_overflow]: isOverflow,
      })}
    >
      <ins>{labelSalePrice}</ins>
      {elementVariants}
    </div>
  );

  const strikeTroughPrice =
    isSpecial && showStriketroughPrice ? (
      <del aria-label="Previous price">{labelPrice}</del>
    ) : null;

  const taxMessage = isTaxAppliedMessage ? (
    <span className={style.price__tax_message}>TAX INCL.</span>
  ) : null;

  const discountMessage = weightVariant?.discount_percent ? (
    <span className={style.price__discount}>{`-${discount}`}</span>
  ) : null;

  if (type === 'row') {
    return (
      <div
        className={classNames(style.price, style.price__row_type, className)}
      >
        {priceMoreVariation}
        {strikeTroughPrice}
        {isTaxAppliedMessage && taxMessage}
        {discountMessage}
      </div>
    );
  }

  if (type === 'expanded') {
    return (
      <div
        className={classNames(
          style.price,
          style.price__expanded_type,
          { [style.price__overflow]: isOverflow },
          className,
        )}
      >
        <div
          className={classNames(style.price__column, {
            [style.price__column_overflow]: isOverflow,
          })}
        >
          {priceMoreVariation}
          {isTaxAppliedMessage && taxMessage}
        </div>
        {strikeTroughPrice || discountMessage ? (
          <div className={style.price__column_right}>
            {strikeTroughPrice}
            {discountMessage}
          </div>
        ) : null}
      </div>
    );
  }

  if (type === 'expanded-tax') {
    return (
      <div
        className={classNames(
          style.price,
          style.price__expanded_tax_type,
          {
            [style.price__overflow]: isOverflow,
          },
          className,
        )}
      >
        <div
          className={classNames(style.price__column, {
            [style.price__column_overflow]: isOverflow,
          })}
        >
          {strikeTroughPrice}
          {priceMoreVariation}
        </div>
        {isTaxAppliedMessage && (
          <div className={style.price__column_right}>{taxMessage}</div>
        )}
      </div>
    );
  }

  if (type === 'column-container') {
    return (
      <div className={classNames(style.price, style.price__column, className)}>
        {priceMoreVariation}
        {(strikeTroughPrice || isTaxAppliedMessage) && (
          <div className={style.price__secundary_info}>
            {strikeTroughPrice}
            {strikeTroughPrice && taxMessage && <div className={style.dot} />}
            {isTaxAppliedMessage && taxMessage}
          </div>
        )}
      </div>
    );
  }

  if (type === 'inverted') {
    return (
      <div
        className={classNames(
          style.price,
          style.price__inverted_type,
          className,
        )}
      >
        {priceMoreVariation}
        {(strikeTroughPrice || isTaxAppliedMessage) && (
          <div className={style.price__secundary_info}>
            {strikeTroughPrice}
            {isTaxAppliedMessage && taxMessage}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={classNames(style.price, className)}>
      {strikeTroughPrice}
      <ins>{labelSalePrice}</ins>
    </div>
  );
};

// Render Weight Variants of Product
const ProductVariants: FC<ProductVariantsProps> = props => {
  const { product } = useProductContext();

  if (product.pricing?.tier_method === 'UNIT') {
    return <ProductUnitPricingTiers {...props} />;
  }

  return <ProductWeightVariants {...props} />;
};

// Render Weight Variants of Product
const ProductWeightVariants: FC<ProductVariantsProps> = ({
  className,
  label,
  type = 'default',
}) => {
  const { weightVariant, variants, updateWeightVariant } = useProductContext();
  const handleKeyDown = useKeyboardCarouselNavigation<HTMLDivElement>();
  const buttonRefs = useRef<Array<HTMLDivElement | null>>([]);

  const [variantsSelectOptions, setVariantsSelectOptions] = useState<
    ISelectOption<string>[]
  >([]);

  const [selectedVariantOption, setSelectedVariantOption] = useState<
    ISelectOption<string>
  >({
    label: formatWeightLabel(weightVariant?.type as string),
    value: weightVariant?.id!,
  });

  useEffect(() => {
    buttonRefs.current = buttonRefs.current.slice(
      0,
      variantsSelectOptions.length,
    );
  }, [variantsSelectOptions]);

  useEffect(() => {
    if (!variants) return;

    const sorted = sortVariantByPrice(variants as Variant[]).map(v => ({
      label: formatWeightLabel(v?.type as string),
      value: v.id as string,
    }));

    setVariantsSelectOptions(sorted);
  }, [variants]);

  const handleVariantChange = (option: ISelectOption<string>) => {
    setSelectedVariantOption(option);
    updateWeightVariant(option);
  };

  const hasMultipleVariants = variantsSelectOptions.length > 1;

  if (type === 'carousel') {
    return (
      <div className={style.variant_carousel__container}>
        {label && <label>{label}</label>}
        <ProductInfoCarousel
          aria-label="Weight variants"
          disableAccess={false}
          role="radiogroup"
        >
          {variantsSelectOptions.map((wv, index) => (
            <div
              aria-checked={selectedVariantOption.value === wv.value}
              aria-label={`Select variant ${wv.label}`}
              className={classNames(style.variants__item, {
                [style.variants__item_active]:
                  selectedVariantOption.value === wv.value,
              })}
              key={wv.value}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleVariantChange(wv);
              }}
              onKeyDown={e =>
                handleKeyDown(e, index, buttonRefs.current, () =>
                  handleVariantChange(variantsSelectOptions[index]),
                )
              }
              ref={el => {
                buttonRefs.current[index] = el;
              }}
              role="radio"
              tabIndex={selectedVariantOption.value === wv.value ? 0 : -1}
            >
              {wv.label}
            </div>
          ))}
        </ProductInfoCarousel>
      </div>
    );
  }

  return (
    <div
      className={classNames(style.variant_select_container, className, {
        [style.variant_select_container_overflow]: !hasMultipleVariants,
      })}
    >
      {type === 'default' && <span aria-hidden="true">/&nbsp;</span>}
      {hasMultipleVariants ? (
        <Select
          className={style.variant_select}
          defaultValue={selectedVariantOption}
          menuClassName={style.variant_select__menu}
          name="weight_variant"
          onChange={handleVariantChange}
          options={variantsSelectOptions}
          placement="top-start"
          value={selectedVariantOption}
        />
      ) : (
        <span className={style.variation__message}>
          {weightVariant?.type && weightVariant.type !== '0_unit'
            ? formatWeightLabel(weightVariant.type)
            : 'Not provided'}
        </span>
      )}
    </div>
  );
};

// Render Unit Pricing Tiers of Product
const ProductUnitPricingTiers: FC<ProductVariantsProps> = ({
  className,
  label,
  type = 'default',
}) => {
  const buttonRefs = useRef<Array<HTMLDivElement | null>>([]);
  const { product, unitPricingTiers, unitPricingTier, updateUnitPricingTier } =
    useProductContext();
  const handleKeyDown = useKeyboardCarouselNavigation<HTMLDivElement>();

  const unitPricingTiersOptions = useMemo(
    () =>
      unitPricingTiers?.map(({ qty, price }) => {
        const totalPrice = price * qty;
        return {
          label: `${qty} for $${currencyFormat(totalPrice * 100)}`,
          value: { qty, price },
        };
      }) ?? [],
    [product],
  );

  const selectedTier = useMemo(() => {
    return find(
      unitPricingTiersOptions,
      option =>
        option.value.qty === unitPricingTier?.qty &&
        option.value.price === unitPricingTier?.price,
    );
  }, [unitPricingTiersOptions, unitPricingTier]);

  useEffect(() => {
    buttonRefs.current = buttonRefs.current.slice(
      0,
      unitPricingTiersOptions.length,
    );
  }, [unitPricingTiersOptions]);

  const hasMultipleTiers = unitPricingTiersOptions?.length > 1;

  const handleSelectedVariation = (
    event: MouseEvent<Element, globalThis.MouseEvent>,
    option: UnitPricingTier,
  ) => {
    event?.stopPropagation();
    event?.preventDefault();

    updateUnitPricingTier(option);
  };

  const handleTierChange = (option: UnitPricingTier) => {
    updateUnitPricingTier(option);
  };

  if (type === 'carousel') {
    return (
      <div className={style.variant_carousel__container}>
        {label && <label>{label}</label>}
        <ProductInfoCarousel
          aria-label="Unit pricing tiers"
          disableAccess={false}
          role="radiogroup"
        >
          {unitPricingTiersOptions?.map((wv, index) => {
            const isSelected =
              wv.value.qty === unitPricingTier?.qty &&
              wv.value.price === unitPricingTier?.price;

            return (
              <div
                aria-checked={isSelected}
                aria-label={`Select tier: ${wv.label}`}
                className={classNames(style.variants__item, {
                  [style.variants__item_active]:
                    wv.value.qty === selectedTier?.value.qty &&
                    wv.value.price === selectedTier?.value.price,
                })}
                key={`${wv.value?.price}-${wv.value?.qty}`}
                onClick={e => handleSelectedVariation(e, wv.value)}
                onKeyDown={e =>
                  handleKeyDown(e, index, buttonRefs.current, () =>
                    updateUnitPricingTier(unitPricingTiersOptions[index].value),
                  )
                }
                ref={el => {
                  buttonRefs.current[index] = el;
                }}
                role="radio"
                tabIndex={isSelected ? 0 : -1}
              >
                {wv.label}
              </div>
            );
          })}
        </ProductInfoCarousel>
      </div>
    );
  }

  return (
    <div
      className={classNames(style.variant_select_container, className, {
        [style.variant_select_container_overflow]: !hasMultipleTiers,
      })}
    >
      {type === 'default' && <span>/&nbsp;</span>}
      {unitPricingTiersOptions?.length > 1 ? (
        <Select
          className={style.variant_select}
          defaultValue={selectedTier}
          menuClassName={style.variant_select__menu}
          name="weight_variant"
          onChange={({ value }) => handleTierChange(value)}
          options={unitPricingTiersOptions}
          placement="top-start"
          value={selectedTier}
        />
      ) : (
        <span className={style.variation__message}>{selectedTier?.label}</span>
      )}
    </div>
  );
};

// Render Carousel With Info Product
const ProductInfoCarousel: FC<{
  className?: string;
  classWrapper?: string;
  direction?: 'horizontal' | 'vertical';
  children: ReactNode[];
  ariaLabel?: string;
  role?: string;
  disableAccess?: boolean;
}> = ({
  className,
  classWrapper,
  children,
  direction = 'horizontal',
  ariaLabel,
  role,
  disableAccess = true,
}) => {
  const isVertidcalDir = direction === 'vertical';

  const cancelEvent = e => {
    e.preventDefault();
    e.stopPropagation();
    return;
  };

  return (
    <div
      className={classNames(style.product__info_carousel_wrapper, classWrapper)}
    >
      <Swiper
        aria-label={ariaLabel}
        className={classNames(style.product__info_carousel, className, {
          [style.product__info_carousel_vertical]: isVertidcalDir,
        })}
        direction={direction}
        freeMode
        grabCursor
        mousewheel
        role={role}
        simulateTouch
        slidesPerView={'auto'}
        spaceBetween={4}
      >
        {children.map((child, index) => (
          <SwiperSlide
            className={classNames({
              [style.product__swipper_slide]: isVertidcalDir,
            })}
            key={index}
            onClick={e => {
              if (disableAccess) {
                cancelEvent(e);
              }
            }}
            role={disableAccess ? 'presentation' : undefined}
            style={{
              width: 'auto',
            }}
            tabIndex={disableAccess ? 0 : undefined}
          >
            {child}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

// Render Reviews and Rating of Product
const ProductReview: FC<ProductReviewProps> = ({
  className,
  label,
  rating,
  reviews,
}) => {
  return (
    <div className={classNames(style.review__container, className)}>
      {label && <label>{label}</label>}
      <div className={style.review__info}>
        <Icon name={rating > 0 ? 'icon_star' : 'start'} />
        <span>{rating}</span>
        <p>{` (${reviews} reviews)`}</p>
      </div>
    </div>
  );
};

// Render Favorite Action of Product
const ProductActionFavorite: FC<IProductProps> = ({ className, product }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const { wishlistActive } = useFeaturedFlag();
  const { measureAddToWishlistGA4 } = useAnalytics();
  const { favProducts, addFav, removeFav } = useWishList();

  useEffect(() => {
    const p = find(favProducts, p => p.id == product.id);
    if (p !== undefined) {
      setIsFavorite(true);
      return;
    }
    setIsFavorite(false);
  }, [favProducts]);

  const handleFavorite = (product: GProduct) => {
    if (isFavorite) {
      removeFav(product.id);
      return;
    }
    measureAddToWishlistGA4(product);
    addFav(product);
  };

  if (!wishlistActive) {
    return null;
  }

  return (
    <Button
      ariaLabel={isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'}
      className={classNames(style.favorite_action, className)}
      color="link"
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        handleFavorite(product);
      }}
      size="sm"
    >
      <Icon
        aria-hidden="true"
        name={isFavorite ? 'favorite-solid' : 'favorite'}
      />
    </Button>
  );
};

// Render The Counter Of Added Products
const ProductIconShopping: FC<{ className?: string; quantity: number }> = ({
  className,
  quantity,
}) => {
  return (
    <div className={classNames(style.icon__shopping, className)}>
      <IconSvg name="shopping" />
      {quantity ? <span className={style.qty__badge}>{quantity}</span> : null}
    </div>
  );
};

interface ProductLinkWrapperProps {
  className?: string;
  product: GProduct;
  analyticsListName?: string;
  isInternalWrapper?: boolean;
  children: ReactNode;
}

interface ProductInfoProps {
  className?: string;
  label?: string;
  info?: string | null;
  icon?: string;
  showLabel?: boolean;
  type?: 'default' | 'filled' | 'solid';
}

interface ProductInfoPackProps {
  className?: string;
  compoundList: CompoundType[];
  label?: string;
  showIcon?: boolean;
}
interface ProductPriceProps {
  className?: string;
  weightVariant: Variant;
  elementVariants?: ReactNode;
  isSpecial?: boolean;
  isOverflow?: boolean;
  type?:
    | 'default'
    | 'expanded'
    | 'row'
    | 'inverted'
    | 'column-container'
    | 'expanded-tax';
}
interface ProductVariantsProps {
  className?: string;
  label?: string;
  type?: 'default' | 'carousel';
}
interface ProductReviewProps {
  className?: string;
  label?: string;
  rating: number;
  reviews: number;
}
interface ProductBadgeContainerProps {
  product: GProduct;
  weightVariant: Variant;
  className?: string;
}

interface ProductInfoListProps {
  compoundList: CompoundType[];
  badgeMode?: 'filled' | 'default' | 'solid';
  badgeClass?: string;
  initDot?: boolean;
}

export {
  ProductLinkWrapper,
  ProductBadgeContainer,
  ProductCannabinoidProfile,
  ProductImage,
  ProductName,
  ProductCategory,
  ProductPrice,
  ProductReview,
  ProductActionFavorite,
  ProductVariants,
  ProductInfoPack,
  ProductInfo,
  ProductIconShopping,
  ProductInfoCarousel,
  ProductUnitPricingTiers,
};
